import { useEffect, useRef, useState } from "react";
import Button, { ButtonProps } from "../Button";
import Progress from "../Progress";
import classNames from "classnames";
import Icon from "../Icon";
import { truncateToFit } from "@/lib/textTruncateHelpers";

export interface FileUploadProps {
  overline?: string;
  label?: string;
  subtitle?: string;
  buttonText?: string;
  handleUpload(file: File): void;
  accept?: string;
  loading?: boolean;
  errorMsg?: string;
  variant?: ButtonProps["variant"];
  icon?: ButtonProps["icon"];
  maxSizeMB?: number;
  onError?: (error: string) => void;
  disabled?: boolean;
  /** When provided, a clear button is shown after a successful upload */
  onClear?: () => void;
}

interface FileValidationError {
  message: string;
}

function parseStrToArray(str: string): Array<string> {
  try {
    const arr = JSON.parse(str);
    return Array.isArray(arr) ? arr : [str];
  } catch (e) {
    return [str];
  }
}

const ErrorMessage = ({ message }: { message: string }) => (
  <p className="text-sys-brand-error text-body-small pt-1 px-4" role="alert" data-testid="file-upload-error">
    {message}
  </p>
);

const ErrorMessages = ({ message }: { message: string }) => {
  const array = parseStrToArray(message);
  return array.map((errorMsg: string, i) => <ErrorMessage key={i} message={errorMsg} />);
};

const FileDisplay = ({
  fileName,
  label,
  containerWidth,
  fontRef
}: {
  fileName?: string;
  label: string;
  containerWidth: number;
  fontRef: React.RefObject<HTMLElement | null>;
}) => {
  const text = fileName || label;
  const displayText =
    fileName && fontRef.current && containerWidth > 0 ? truncateToFit(text, containerWidth, fontRef.current) : text;
  return (
    <p
      className={classNames(
        "text-sys-brand-on-surface whitespace-nowrap overflow-hidden",
        fileName ? "text-body-large-semibold" : "text-body-large"
      )}
      title={text}
    >
      {displayText}
    </p>
  );
};

const validateFile = (file: File, accept?: string, maxSizeMB?: number): FileValidationError | null => {
  if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
    return {
      message: `File size must be less than ${maxSizeMB}MB`
    };
  }

  if (accept) {
    const acceptedTypes = accept.split(",").map((type) => type.trim());
    const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
    const fileType = file.type.toLowerCase();

    const isValidType = acceptedTypes.some((type) => {
      if (type.startsWith(".")) {
        return fileExtension === type.toLowerCase();
      }
      return fileType.startsWith(type.replace("*", ""));
    });

    if (!isValidType) {
      return {
        message: `File type not allowed. Accepted types: ${accept}`
      };
    }
  }

  return null;
};

export default function FileUpload({
  overline = "",
  label = "Choose a File to Upload",
  subtitle = "",
  buttonText = "Upload",
  variant = "filledTonal",
  handleUpload,
  accept,
  loading,
  errorMsg,
  icon = "upload",
  maxSizeMB,
  onError,
  disabled = false,
  onClear
}: FileUploadProps) {
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fontRef = useRef<HTMLSpanElement>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | undefined>(undefined);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(container);
    setContainerWidth(container.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUploadedFileName(undefined);
    // Reset the hidden input so the same file can be re-selected
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = "";
    }
    onClear?.();
  };

  const handleClick = () => {
    if (!disabled) {
      hiddenInputRef?.current?.click();
    }
  };

  const processFile = (file: File) => {
    const validationError = validateFile(file, accept, maxSizeMB);
    if (validationError) {
      onError?.(validationError.message);
      return;
    }

    setUploadedFileName(file.name);
    handleUpload(file);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const showSuccess = !!uploadedFileName && !loading && !errorMsg;

  return (
    <div className="w-full">
      <div
        className={classNames(
          "border border-solid w-full rounded px-4 py-2 flex flex-row items-center min-h-[3.5rem]",
          {
            "border-sys-brand-error": !!errorMsg,
            "border-sys-brand-outline": !errorMsg,
            "cursor-pointer": !disabled,
            "cursor-not-allowed opacity-50": disabled
          }
        )}
        onClick={handleClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={label}
        aria-disabled={disabled}
        aria-describedby={subtitle ? "upload-subtitle" : undefined}
      >
        <div className="flex flex-row items-center w-full min-w-0">
          <div ref={containerRef} className="flex-1 min-w-0 overflow-hidden">
            <span
              ref={fontRef}
              className="text-body-large-semibold"
              style={{ position: "absolute", visibility: "hidden", pointerEvents: "none" }}
              aria-hidden
            />
            {!!overline && <p className="text-sys-brand-on-surface-variant text-label-large">{overline}</p>}
            <FileDisplay fileName={uploadedFileName} label={label} containerWidth={containerWidth} fontRef={fontRef} />
            {!!subtitle && (
              <p id="upload-subtitle" className="text-sys-brand-on-surface-variant text-body-medium">
                {subtitle}
              </p>
            )}
          </div>
          <div className="ml-4 flex items-center gap-1">
            {showSuccess ? (
              <>
                <Icon name="check_circle" className="text-extended-success-brand-color" size={28} />
                {onClear && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="flex items-center justify-center p-1 rounded-full hover:bg-sys-brand-surface-container-high transition-colors"
                    aria-label="Remove uploaded file"
                    title="Remove file"
                  >
                    <Icon name="delete" size={20} className="text-sys-brand-on-surface-variant" />
                  </button>
                )}
              </>
            ) : loading ? (
              <Progress />
            ) : (
              <Button type="button" variant={variant} icon={icon} disabled={disabled}>
                {buttonText}
              </Button>
            )}
          </div>
        </div>
      </div>
      <input
        className="hidden"
        type="file"
        ref={hiddenInputRef}
        onChange={handleChange}
        data-testid="file-upload"
        accept={accept}
        disabled={disabled}
        aria-hidden="true"
      />
      {!!errorMsg && <ErrorMessages message={errorMsg} />}
    </div>
  );
}
